import { Alert, Button, HeaderBlock, ModalV2, Prompt, TextTooltip } from '@components'
import { ContactType, getContactPageUrl, getInvoicingPageUrl, InvoiceStatus } from '@constants'
import { PermissionSet, useBusinessContext, useFeature, usePermission } from '@context'
import { DocumentFormModal } from '@pages/DocumentPage/DocumentForm'
import {
  acceptInvoice,
  deleteInvoice,
  fetchAccount,
  IInvoice,
  kraviaOnboardStatus,
  setInvoiceAsCreditLoss,
  setInvoiceAsPaid,
  setInvoiceAsUnpaid
} from '@query'
import { isGermanBusinessCountry } from './xrechnungDelivery'
import { defaultValuesForSalesInvoiceDocument, formatDate } from '@utils'
import React, { useMemo, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import {
  FaCheckCircle,
  FaCog,
  FaDownload,
  FaEnvelope,
  FaFeatherAlt,
  FaMinusCircle,
  FaMinusSquare,
  FaPaperPlane,
  FaPen,
  FaQuestionCircle,
  FaReply,
  FaTrash
} from 'react-icons/fa'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { invoiceQueryOptions, useInvoice } from '@root/query/hooks/invoices'
import { Link } from 'react-router-dom'
import { InvoiceForm } from './InvoiceForm'

import { InvoiceReadyToSendIcon } from './invoiceIcons'
import {
  InvoiceRecurrenceDisabledBadge,
  InvoiceStatusBadge,
  InvoiceSentBadge,
  InvoiceNotSentBadge,
  InvoiceReadyToSendChip,
  PostDueStatusBadges,
  InvoiceWatchOnBadge
} from './InvoiceStatusBadge'
import {
  AttachmentSection,
  ContentFooter,
  ContentFooterStatus,
  DetailMetaGrid,
  DetailSection,
  HeaderMeta,
  HeaderMetaStrong,
  ScrollableContent,
  SectionLabel,
  StyledProductDetails
} from './InvoicingDetails.styled'
import { SendInvoiceForm } from './SendInvoiceForm'
import InvoiceRecurrenceEditSettings from './InvoiceRecurrenceEditSettings'
import InvoiceRecurrenceInfo from './InvoiceRecurrenceInfo'
import { SoftReminderTracker } from '@pages/InvoicingPage/DebtCollection/trackerMessages/SoftReminderTracker.tsx'
import { DebtCollectionStatusMessage } from '@pages/InvoicingPage/DebtCollection/trackerMessages/DebtCollectionStatusMessage.tsx'
import {
  postDueFromInvoice,
  visiblePostDueSchedule,
  isUpcomingWatchSchedule
} from '@pages/InvoicingPage/postDueUi.ts'
import { DueDateStatusIcon } from './combinedStatus'
import { isOverdue } from '@root/utils/invoicingHelpers'
import { AttachmentSelector } from '@containers/AttachmentSelector'
import { TagDisplay } from '@components/TagDisplay/TagDisplay'
import { InvoiceDetailField } from './InvoiceDetailField'
import { InvoiceDocumentLinkField, InvoicePaidAtField } from './InvoiceDetailSharedFields'
import { InvoiceLineItems } from './InvoiceLineItemsTable'
import InvoicePostDueAutomationSettings from './InvoicePostDueAutomationSettings'
import { PostDueAutomationDetailAlerts } from './PostDueAutomationDetailAlerts'

interface Props {
  invoiceId: number
  onClose: () => void
}

export const InvoiceDetails: React.FC<Props> = ({ invoiceId, onClose }) => {
  const queryClient = useQueryClient()
  const { slug, data: business } = useBusinessContext()
  const isGermanBusiness = isGermanBusinessCountry(business?.country)
  const [t] = useTranslation()
  const [showDelete, setShowDelete] = useState(false)
  const [showDocumentForm, setShowDocumentForm] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [showSend, setShowSend] = useState(false)
  const [showCreditNote, setShowCreditNote] = useState(false)
  const [showRecurenceEdit, setShowRecurenceEdit] = useState(false)
  const [showPostDueAutomation, setShowPostDueAutomation] = useState(false)
  const hasEditPermission = usePermission(PermissionSet.INVOICING_EDIT)
  const hasBookkeepingEditPermission = usePermission(PermissionSet.BOOKKEEPING_EDIT)
  const hasBookkeepingViewPermission = usePermission(PermissionSet.BOOKKEEPING_VIEW)
  const { enabled: kraviaEnabled } = useFeature('kravia')

  const { data } = useInvoice({ invoiceId }, { enabled: !showDelete })

  const { data: kraviaData } = useQuery({
    queryKey: [slug, 'kravia_onboard_status'],
    queryFn: () => kraviaOnboardStatus({ slug }),
    enabled: kraviaEnabled && Boolean(slug)
  })

  const { data: creditNoteForData } = useInvoice(
    { invoiceId: data?.is_credit_note_for },
    { enabled: Boolean(data?.is_credit_note_for) }
  )

  const receivablesAccountId = business?.account_trade_receivables
  const { data: receivablesAccount } = useQuery({
    queryKey: [slug, 'accounts', receivablesAccountId],
    queryFn: () => fetchAccount({ slug, accountId: Number(receivablesAccountId) }),
    enabled: Boolean(slug && data?.is_credit_note_for && receivablesAccountId)
  })

  const creditNotesResults = useQueries({
    queries: (data?.credit_notes || []).map(creditNoteId => ({
      ...invoiceQueryOptions({ slug, invoiceId: creditNoteId })
    }))
  })
  const creditNotes = creditNotesResults.every(result => result.isSuccess)
    ? creditNotesResults.map(result => result.data)
    : []

  // Do not await: waiting for infinite-list refetch freezes the accept UI.
  const invalidateInvoiceQueries = () => {
    void queryClient.invalidateQueries({ queryKey: [slug, 'invoices'] })
    void queryClient.invalidateQueries({ queryKey: [slug, 'combined_invoices'] })
  }

  const { mutateAsync: deleteMutation, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteInvoice({ slug, invoiceId }),
    ...{
      onSuccess: () => {
        invalidateInvoiceQueries()
        onClose()
        setShowDelete(false)
      }
    }
  })

  const { mutateAsync: acceptMutation, isPending: isAccepting } = useMutation({
    mutationFn: () => acceptInvoice({ slug, invoiceId }),
    ...{
      onSuccess: acceptedInvoice => {
        // Update detail cache immediately so Accept is not re-enabled on a stale draft
        // while list invalidation refreshes in the background.
        queryClient.setQueryData<IInvoice>([slug, 'invoices', invoiceId], current => {
          if (acceptedInvoice && typeof acceptedInvoice === 'object') {
            return acceptedInvoice as IInvoice
          }
          return current ? { ...current, status: InvoiceStatus.ACCEPTED } : current
        })
        invalidateInvoiceQueries()
        void queryClient.invalidateQueries({ queryKey: [slug], exact: true })
      }
    }
  })

  const { mutateAsync: setAsPaidMutation } = useMutation({
    mutationFn: () => setInvoiceAsPaid({ slug, invoiceId }),
    ...{
      onSuccess: invalidateInvoiceQueries
    }
  })

  const { mutateAsync: setAsCreditLossMutation } = useMutation({
    mutationFn: () => setInvoiceAsCreditLoss({ slug, invoiceId }),
    ...{
      onSuccess: invalidateInvoiceQueries
    }
  })

  const { mutateAsync: setAsUnpaidMutation } = useMutation({
    mutationFn: () => setInvoiceAsUnpaid({ slug, invoiceId }),
    ...{
      onSuccess: invalidateInvoiceQueries
    }
  })

  const defaultValuesForDocumentForm = useMemo(() => {
    return defaultValuesForSalesInvoiceDocument(
      data,
      business?.account_trade_receivables,
      receivablesAccount,
      business?.country_config
    )
  }, [business?.account_trade_receivables, business?.country_config, data, receivablesAccount])

  const isDraft = data?.status === InvoiceStatus.DRAFT
  const isRecurringActively = data?.is_recurrence_active
  const isTypeOfRecurring = data?.is_recurrence_applied
  const isRecurrenceExceeded = data?.is_recurrence_end_exceeded
  const showRecurrenceDisabledBadge =
    !isRecurringActively && !isRecurrenceExceeded && isTypeOfRecurring
  const showRecurrenceInfo = isTypeOfRecurring && !showRecurrenceDisabledBadge
  const isPaid = data?.status === InvoiceStatus.PAID
  const isCreditLoss = data?.status === InvoiceStatus.CREDIT_LOSS
  const isSent = data?.last_delivery_at !== null
  const isBusinessReceiver = data?.receiver_info?.type === ContactType.BUSINESS
  const showBuyerReferenceRow = isGermanBusiness && isBusinessReceiver
  const invoiceBuyerReference = data?.buyer_reference?.trim() || ''
  const contactBuyerReference = data?.receiver_info?.xrechnung_buyer_reference?.trim() || ''
  const displayedBuyerReference = invoiceBuyerReference || contactBuyerReference
  const buyerReferenceFromContact = !invoiceBuyerReference && !!contactBuyerReference
  const postDue = postDueFromInvoice(data)
  const manualCollectionAvailable =
    hasEditPermission && Boolean(kraviaData?.kravia_enabled && kraviaData.kravia_verified)
  const automaticCollectionEnabled = Boolean(business?.is_automatic_debt_collection_enabled)
  const schedule = visiblePostDueSchedule(data, {
    kraviaFeature: kraviaEnabled,
    manualCollectionAvailable,
    automaticCollectionEnabled
  })
  const inDebtCollection = postDue.kraviaMark
  const watchOn = isUpcomingWatchSchedule(data)
  const showSchedule = schedule.length > 0 && !watchOn
  const isSendable = !!data?.is_sendable
  const showReadyToSendState =
    !isDraft &&
    !isSent &&
    !isPaid &&
    !isCreditLoss &&
    isSendable &&
    hasEditPermission &&
    !isTypeOfRecurring &&
    !inDebtCollection &&
    !showSchedule

  const headerMeta = useMemo(() => {
    if (!data) return undefined

    const hasNumber = Boolean(data.invoice_number && data.status !== InvoiceStatus.DRAFT)
    const parts: React.ReactNode[] = []

    if (hasNumber) {
      parts.push(
        <span key="number">
          {t('invoicing.details.number')} <HeaderMetaStrong>{data.invoice_number}</HeaderMetaStrong>
        </span>
      )
    }

    if (data.reference && data.status !== InvoiceStatus.DRAFT) {
      if (parts.length > 0) {
        parts.push(<span key="dot">·</span>)
      }
      parts.push(
        <span key="reference">
          {t('invoicing.details.reference')} <HeaderMetaStrong>{data.reference}</HeaderMetaStrong>
        </span>
      )
    }

    if (parts.length === 0) return undefined
    return <HeaderMeta>{parts}</HeaderMeta>
  }, [data, t])

  const menuItems = React.useMemo(() => {
    const menuItems = []

    if (isDraft) {
      if (hasEditPermission) {
        menuItems.push({
          Icon: FaPen,
          name: t('invoicing.details.edit'),
          intent: 'default',
          group: 'actions',
          onClick: () => setShowEdit(true)
        })

        menuItems.push({
          Icon: FaCheckCircle,
          name: t('invoicing.details.accept'),
          intent: 'default',
          group: 'actions',
          disabled: isAccepting,
          onClick: () => !isAccepting && acceptMutation()
        })
      }
    } else {
      hasBookkeepingEditPermission &&
        menuItems.push({
          Icon: FaFeatherAlt,
          name: t('invoicing.details.createDocument'),
          intent: 'default',
          group: 'actions',
          onClick: () => setShowDocumentForm(true)
        })

      hasEditPermission &&
        menuItems.push({
          Icon: FaReply,
          name: t('invoicing.details.refund'),
          intent: 'default',
          group: 'actions',
          onClick: () => setShowCreditNote(true)
        })
    }

    if (!isDraft && isSendable && hasEditPermission) {
      menuItems.push({
        Icon: FaPaperPlane,
        name: isSent
          ? t('invoicing.debtCollection.register.sendReminder')
          : t('invoicing.details.send'),
        intent: 'default',
        group: 'actions',
        onClick: () => setShowSend(true)
      })
    }

    if (isPaid || isCreditLoss) {
      hasEditPermission &&
        menuItems.push({
          Icon: FaMinusSquare,
          name: t('invoicing.actions.markUnpaid'),
          intent: 'default',
          group: 'actions',
          onClick: () => setAsUnpaidMutation()
        })
    } else if (!isDraft && hasEditPermission) {
      menuItems.push({
        Icon: FaCheckCircle,
        name: t('invoicing.actions.markPaid'),
        intent: 'default',
        group: 'actions',
        onClick: () => setAsPaidMutation()
      })
      menuItems.push({
        Icon: FaMinusCircle,
        name: t('invoicing.actions.markCreditLoss'),
        intent: 'default',
        group: 'actions',
        onClick: () => setAsCreditLossMutation()
      })
    }

    if (isRecurringActively && !isDraft && hasEditPermission) {
      menuItems.push({
        Icon: FaCog,
        name: t('invoicing.actions.modifyRecurrence'),
        intent: 'default',
        group: 'actions',
        onClick: () => setShowRecurenceEdit(true)
      })
    }

    if (hasEditPermission && !data?.is_credit_note_for && !isPaid && !isCreditLoss) {
      menuItems.push({
        Icon: FaEnvelope,
        name: t('invoicing.details.postDueAutomation.prompt.title'),
        intent: 'default',
        group: 'actions',
        onClick: () => setShowPostDueAutomation(true)
      })
    }

    if (data?.xrechnung_ubl_xml) {
      const xrechnungXmlUrl = data.xrechnung_ubl_xml
      menuItems.push({
        Icon: FaDownload,
        name: t('invoicing.details.downloadXrechnungXml'),
        intent: 'default',
        group: 'actions',
        onClick: () => {
          const link = document.createElement('a')
          link.href = xrechnungXmlUrl
          link.target = '_blank'
          link.rel = 'noopener noreferrer'
          link.download = ''
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      })
    }

    hasEditPermission &&
      menuItems.push({
        Icon: FaTrash,
        name: t('invoicing.details.delete'),
        intent: 'danger',
        group: 'danger',
        onClick: () => setShowDelete(true)
      })

    return menuItems
  }, [
    acceptMutation,
    data?.is_credit_note_for,
    data?.xrechnung_ubl_xml,
    hasBookkeepingEditPermission,
    hasEditPermission,
    isAccepting,
    isCreditLoss,
    isDraft,
    isPaid,
    isRecurringActively,
    isSendable,
    isSent,
    setAsCreditLossMutation,
    setAsPaidMutation,
    setAsUnpaidMutation,
    t
  ])

  const actionButton = useMemo(() => {
    if (inDebtCollection) return null
    if (isDraft && hasEditPermission)
      return (
        <Button
          data-test="invoice-form-accept-invoice-button"
          intent="success"
          showSpinner={isAccepting}
          disabled={isAccepting}
          icon={<FaCheckCircle />}
          onClick={() => acceptMutation()}
        >
          {t('invoicing.actions.accept')}
        </Button>
      )

    if (inDebtCollection) return null

    if (!isSent && !isTypeOfRecurring && hasEditPermission && isSendable)
      return (
        <Button intent="primary" icon={<FaPaperPlane />} onClick={() => setShowSend(true)}>
          {t('invoicing.actions.send')}
        </Button>
      )

    if (!(isPaid || isCreditLoss) && isSent && hasEditPermission)
      return (
        <Button intent="success" icon={<FaCheckCircle />} onClick={() => setAsPaidMutation()}>
          {t('invoicing.actions.markPaid')}
        </Button>
      )

    return null
  }, [
    acceptMutation,
    data,
    hasEditPermission,
    inDebtCollection,
    isAccepting,
    isCreditLoss,
    isDraft,
    isPaid,
    isSendable,
    isSent,
    isTypeOfRecurring,
    setAsPaidMutation,
    t
  ])

  const isDeletable = data?.is_deletable !== false

  if (!data) return null
  return (
    <StyledProductDetails>
      <HeaderBlock
        header={
          data?.is_credit_note_for
            ? t('invoicing.details.creditNoteTitle')
            : t('invoicing.details.title')
        }
        meta={headerMeta}
        menuItems={menuItems}
        badges={
          <>
            <InvoiceStatusBadge status={data?.status} />
            {showRecurrenceDisabledBadge && <InvoiceRecurrenceDisabledBadge />}
            {showReadyToSendState && <InvoiceReadyToSendChip />}
            {!isDraft &&
              !showReadyToSendState &&
              (data?.last_delivery_at ? <InvoiceSentBadge /> : <InvoiceNotSentBadge />)}
            {(postDue.reminderMark || (kraviaEnabled && postDue.kraviaMark)) && (
              <PostDueStatusBadges
                reminder={postDue.reminderMark}
                kravia={kraviaEnabled && postDue.kraviaMark}
              />
            )}
            {watchOn && data && <InvoiceWatchOnBadge invoice={data} />}
          </>
        }
        withDivider
      />

      <ModalV2
        header={t('invoicing.form.titleModify')}
        isVisible={showEdit}
        handleOnClose={() => setShowEdit(false)}
        width={800}
        height={900}
      >
        <InvoiceForm
          editInvoiceId={data.id}
          defaultValues={data}
          onSubmit={() => setShowEdit(false)}
        />
      </ModalV2>

      <ModalV2
        header={t('invoicing.form.titleModify', { invoiceNumber: data?.invoice_number })}
        isVisible={showCreditNote}
        handleOnClose={() => setShowCreditNote(false)}
        width={800}
        height={900}
      >
        <InvoiceForm
          defaultValues={data}
          isCreditNoteFor={data?.id}
          onSubmit={() => setShowCreditNote(false)}
        />
      </ModalV2>

      <ModalV2
        header={t('invoicing.send.title')}
        isVisible={showSend}
        handleOnClose={() => setShowSend(false)}
      >
        <SendInvoiceForm
          invoice={data}
          onSubmit={() => setShowSend(false)}
          onCancel={() => setShowSend(false)}
        />
      </ModalV2>

      <InvoiceRecurrenceEditSettings
        invoice={data}
        handleOnClose={() => setShowRecurenceEdit(false)}
        showPrompt={showRecurenceEdit}
      />

      {data && (
        <InvoicePostDueAutomationSettings
          invoice={data}
          showPrompt={showPostDueAutomation}
          handleOnClose={() => setShowPostDueAutomation(false)}
        />
      )}

      <ScrollableContent>
        {showReadyToSendState && (
          <DetailSection>
            <Alert
              type="success"
              title={t('invoicing.details.notDeliveredTitle')}
              description={t('invoicing.details.notDeliveredBody', {
                recipient: data?.receiver_info?.name
              })}
              marginBottom={0}
            />
          </DetailSection>
        )}

        {data && (
          <PostDueAutomationDetailAlerts
            invoice={data}
            onOpenPostDueSettings={() => setShowPostDueAutomation(true)}
          />
        )}

        {showSchedule && (
          <DetailSection>
            <SoftReminderTracker
              invoice={data}
              manualCollectionAvailable={manualCollectionAvailable}
              automaticCollectionEnabled={automaticCollectionEnabled}
            />
          </DetailSection>
        )}

        {kraviaEnabled && inDebtCollection && (
          <DetailSection>
            <DebtCollectionStatusMessage invoice={data} />
          </DetailSection>
        )}

        {showRecurrenceInfo && (
          <DetailSection>
            <InvoiceRecurrenceInfo invoice={data} />
          </DetailSection>
        )}

        <DetailSection>
          <DetailMetaGrid>
            {data?.is_credit_note_for && (
              <InvoiceDetailField label={t('invoicing.details.creditNoteNumber')} fullWidth>
                <Link to={getInvoicingPageUrl(slug, { id: creditNoteForData?.id, type: 'sales' })}>
                  {creditNoteForData?.friendly_name}
                </Link>
              </InvoiceDetailField>
            )}

            {creditNotes.length > 0 && (
              <InvoiceDetailField label={t('invoicing.details.creditNoteDone')} fullWidth>
                {creditNotes.map(({ id, friendly_name }) => (
                  <Link
                    key={`credit-note-${id}`}
                    to={getInvoicingPageUrl(slug, { id, type: 'sales' })}
                  >
                    {friendly_name}
                  </Link>
                ))}
              </InvoiceDetailField>
            )}

            <InvoiceDetailField
              label={t('invoicing.details.receiver')}
              data-test="invoice-details-receiver"
            >
              <Link to={getContactPageUrl(slug, { id: data?.receiver })}>
                {data?.receiver_info?.name}
              </Link>
            </InvoiceDetailField>

            <InvoiceDetailField
              label={t('invoicing.details.invoicingDate')}
              data-test="invoice-details-date"
            >
              {formatDate(data?.invoicing_date)}
            </InvoiceDetailField>

            <InvoiceDetailField
              label={t('invoicing.details.dueDate')}
              valueContainer
              data-test="invoice-details-due-date"
            >
              {formatDate(data?.due_date)}
              {isOverdue(data) && <DueDateStatusIcon />}
            </InvoiceDetailField>

            {data?.last_delivery_at && data?.delivery_method && (
              <InvoiceDetailField
                label={t('invoicing.details.deliveryMethod')}
                data-test="invoice-details-delivery-method"
              >
                {t(`invoicing.deliveryMethod.${data.delivery_method}`)}
              </InvoiceDetailField>
            )}

            <InvoicePaidAtField
              label={t('invoicing.details.paidAt')}
              isPaid={isPaid}
              bankTransaction={data?.bank_transaction}
              paymentDate={data?.payment_date}
            />

            {hasBookkeepingViewPermission && (
              <InvoiceDocumentLinkField
                slug={slug}
                document={data?.document}
                label={t('invoicing.details.document')}
                moveToDocumentLabel={t('invoicing.details.moveToDocument')}
                moveToDraftLabel={t('invoicing.details.moveToDraft')}
              />
            )}

            {showBuyerReferenceRow && (
              <InvoiceDetailField
                label={
                  <>
                    {t('invoicing.form.buyerReferenceBt10')}
                    {buyerReferenceFromContact && (
                      <TextTooltip
                        tooltip={
                          <Trans
                            i18nKey="invoicing.details.buyerReferenceFromContact"
                            values={{ contactName: data?.receiver_info?.name }}
                            components={{
                              contact: <Link to={getContactPageUrl(slug, { id: data?.receiver })} />
                            }}
                          />
                        }
                      >
                        <FaQuestionCircle />
                      </TextTooltip>
                    )}
                  </>
                }
                data-test="invoice-details-buyer-reference"
              >
                {displayedBuyerReference || '-'}
              </InvoiceDetailField>
            )}

            {data?.description && (
              <InvoiceDetailField
                label={t('invoicing.form.description')}
                fullWidth
                data-test="invoice-details-description"
              >
                <span style={{ whiteSpace: 'pre-wrap' }}>{data.description}</span>
              </InvoiceDetailField>
            )}
          </DetailMetaGrid>
        </DetailSection>

        {data?.tag_ids?.length > 0 && (
          <DetailSection>
            <TagDisplay
              tagIds={data.tag_ids}
              label={<SectionLabel>{t('document.tags')}</SectionLabel>}
            />
          </DetailSection>
        )}

        <DetailSection>
          <SectionLabel>{t('invoicing.details.rows')}</SectionLabel>
          <InvoiceLineItems
            rows={data?.rows ?? []}
            totalAmount={data?.total_amount ?? 0}
            totalVatAmount={data?.total_vat_amount ?? 0}
            showColor={Boolean(data?.is_credit_note_for)}
          />
        </DetailSection>

        {data?.pdf?.id && (
          <DetailSection>
            <SectionLabel>{t('invoicing.details.preview')}</SectionLabel>
            <AttachmentSection>
              <AttachmentSelector value={[data?.pdf?.id]} disabled={true} />
            </AttachmentSection>
          </DetailSection>
        )}

        {data.attachments.length > 0 && (
          <DetailSection>
            <SectionLabel>{t('invoicing.details.attachments')}</SectionLabel>
            <AttachmentSection>
              <AttachmentSelector value={data.attachments} disabled={true} />
            </AttachmentSection>
          </DetailSection>
        )}
      </ScrollableContent>

      {actionButton && (
        <ContentFooter $spread={showReadyToSendState}>
          {showReadyToSendState && (
            <ContentFooterStatus>
              <InvoiceReadyToSendIcon />
              <span>
                <strong>{t('invoicing.details.footerNotDeliveredLead')}</strong>{' '}
                {t('invoicing.details.footerNotDeliveredHint')}
              </span>
            </ContentFooterStatus>
          )}
          {actionButton}
        </ContentFooter>
      )}

      <Prompt
        isVisible={showDelete}
        title={t('invoicing.details.deleteTitle')}
        description={
          <>
            <div>
              {isDraft
                ? t('invoicing.details.deleteDescription')
                : t('invoicing.details.deleteNonDraftDescription')}
            </div>
            <Alert
              type="error"
              title={t('invoicing.details.cannotDelete')}
              isVisible={!isDeletable}
              description={t('invoicing.details.cannotDeleteDescription')}
            />
          </>
        }
        buttons={[
          {
            text: t('general.cancel'),
            action: () => setShowDelete(false)
          },
          {
            text: t('general.delete'),
            icon: <FaTrash />,
            intent: 'danger',
            action: () => deleteMutation(),
            showSpinner: isDeleting,
            disabled: !isDeletable || isDeleting
          }
        ]}
        onClose={() => setShowDelete(false)}
      />

      {data && (
        <DocumentFormModal
          isVisible={showDocumentForm}
          onClose={() => setShowDocumentForm(false)}
          defaults={defaultValuesForDocumentForm}
        />
      )}
    </StyledProductDetails>
  )
}
